import { useState, useEffect, useCallback, useRef } from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import {
  Key, SquaresFour, UsersThree, Bell, SignOut, House, Buildings, BellRinging, Target, List, X, GearSix,
} from "@phosphor-icons/react";
import api from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { BRAND } from "@/lib/constants";
import { TierBadge, timeAgo } from "./helpers";

function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const ref = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const unread = items.filter((i) => !i.read).length;

  const markAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  const openNote = async (n) => {
    await api.post(`/notifications/${n.id}/read`);
    setOpen(false);
    load();
    if (n.lead_id) navigate(`/dashboard/leads/${n.lead_id}`);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="notification-bell"
        onClick={() => setOpen((o) => !o)}
        className="relative p-2.5 rounded-sm border border-white/10 text-white/70 hover:text-brand hover:border-brand/40 transition-colors"
      >
        <Bell size={18} weight="duotone" />
        {unread > 0 && (
          <span
            data-testid="notification-count"
            className="absolute -top-1.5 -right-1.5 h-4 min-w-4 px-1 flex items-center justify-center rounded-full bg-brand text-black text-[10px] font-bold"
          >
            {unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-h-[26rem] overflow-y-auto bg-[#101116] border border-white/10 rounded-sm shadow-2xl z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
            <span className="text-sm text-white/80">Notifications</span>
            {unread > 0 && (
              <button onClick={markAll} data-testid="mark-all-read" className="text-xs text-brand hover:text-brand-hover">
                Mark all read
              </button>
            )}
          </div>
          {items.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-white/40">No notifications yet.</p>
          ) : (
            items.map((n) => (
              <button
                key={n.id}
                onClick={() => openNote(n)}
                data-testid={`notification-item-${n.id}`}
                className={`w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/5 transition-colors ${
                  n.read ? "opacity-60" : ""
                }`}
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="text-sm text-white truncate">{n.title}</span>
                  <TierBadge tier={n.tier} />
                </div>
                <p className="text-xs text-white/50 line-clamp-2">{n.body}</p>
                <p className="text-[11px] text-white/30 mt-1">{timeAgo(n.created_at)}</p>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const navItems = [
    { to: "/dashboard", icon: SquaresFour, label: "Overview", end: true },
    { to: "/dashboard/leads", icon: UsersThree, label: "Leads", end: false },
    { to: "/dashboard/prospects", icon: Target, label: "Prospects", end: false },
    { to: "/dashboard/properties", icon: Buildings, label: "Properties", end: false },
    { to: "/dashboard/alerts", icon: BellRinging, label: "Alerts", end: false },
    { to: "/dashboard/settings", icon: GearSix, label: "Settings", end: false },
  ];

  const navClass = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-3 rounded-sm text-sm transition-colors ${
      isActive ? "bg-brand/10 text-brand" : "text-white/60 hover:text-white hover:bg-white/5"
    }`;

  const SidebarBody = ({ onNavigate }) => (
    <>
      <div className="flex items-center gap-2 text-white tracking-[0.2em] text-sm mb-12">
        <Key size={20} weight="duotone" className="text-brand" /> {BRAND.mark}
      </div>
      <nav className="space-y-1 flex-1">
        {navItems.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={`nav-${n.label.toLowerCase()}`}
            onClick={onNavigate}
            className={navClass}
          >
            <n.icon size={18} weight="duotone" /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="space-y-1 pt-4 border-t border-white/5">
        <Link
          to="/"
          data-testid="nav-view-site"
          onClick={onNavigate}
          className="flex items-center gap-3 px-4 py-3 rounded-sm text-sm text-white/60 hover:text-white hover:bg-white/5 transition-colors"
        >
          <House size={18} weight="duotone" /> View site
        </Link>
        <button
          onClick={doLogout}
          data-testid="logout-btn"
          className="w-full flex items-center gap-3 px-4 py-3 rounded-sm text-sm text-white/60 hover:text-white hover:bg-white/5 transition-colors"
        >
          <SignOut size={18} weight="duotone" /> Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col w-64 border-r border-white/5 bg-[#0B0C10] p-6 fixed inset-y-0 left-0">
        <SidebarBody />
      </aside>

      {/* Mobile drawer */}
      {menuOpen && (
        <div className="md:hidden fixed inset-0 z-50" data-testid="dashboard-mobile-menu">
          <button
            type="button"
            aria-label="Close menu"
            className="absolute inset-0 bg-black/70"
            onClick={() => setMenuOpen(false)}
          />
          <aside className="relative flex flex-col w-[min(18rem,85vw)] h-full border-r border-white/5 bg-[#0B0C10] p-6 shadow-2xl">
            <button
              type="button"
              data-testid="dashboard-menu-close"
              aria-label="Close menu"
              onClick={() => setMenuOpen(false)}
              className="absolute top-4 right-4 p-2 rounded-sm border border-white/10 text-white/70 hover:text-white"
            >
              <X size={18} />
            </button>
            <SidebarBody onNavigate={() => setMenuOpen(false)} />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 md:ml-64 min-w-0">
        <header className="sticky top-0 z-30 h-20 border-b border-white/5 bg-background/70 backdrop-blur-md flex items-center justify-between px-4 sm:px-6 gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              data-testid="dashboard-menu-toggle"
              aria-label="Open menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen(true)}
              className="md:hidden flex h-10 w-10 shrink-0 items-center justify-center rounded-sm border border-white/15 text-white hover:border-brand hover:text-brand"
            >
              <List size={20} weight="bold" />
            </button>
            <div className="min-w-0">
              <p className="text-xs text-white/40 uppercase tracking-[0.2em]">Dashboard</p>
              <p className="text-white text-sm truncate">Welcome back, {user?.name || "Lamar"}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <NotificationBell />
            <button onClick={doLogout} className="md:hidden p-2.5 rounded-sm border border-white/10 text-white/70" data-testid="logout-btn-mobile">
              <SignOut size={18} />
            </button>
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
